import { Component, output, input } from '@angular/core';
import { IntegrationGridItem } from './IntegrationGridItem';
import { IntegrationListItem } from './IntegrationListItem';
import { AppIntegration, AppIntegrationFromTo } from "./DashboardIntegrations";
import { UserCombi } from "../model";
import { UserCombiPackageService } from "../services/usercombipackage/usercombipackage.service";

@Component({
  selector: 'Integration',
  imports: [IntegrationGridItem, IntegrationListItem],
  template: `
    @if (type() === 'grid') {
      <IntegrationGridItem
          [firstApp]="firstApp()"
          [secondApp]="secondApp()"
          [description]="description()"
          [href]="href()"
          [hasClickHandler]="hasClickHandler()"
          [integration]="integration()"
          [showHistoryButton]="showHistoryButton()"
          [readonly]="readonly()"
          [showTryNowButton]="showTryNowButton()"
          [status]="getStatus(integration()?.status)"
          (clicked)="onClicked()"
          (orderButtonClicked)="onOrderButtonClicked()"
          (toggleActiveClicked)="onToggleActiveClicked()"
          (runClicked)="onRunClicked($event)"
          (historyClicked)="onHistoryClicked($event)"
          (editClicked)="onEditClicked()"
          (abandonedCartStatisticsClicked)="onAbandonedCartStatisticsClicked()"
          (statisticsClicked)="onStatisticsClicked()"
          (calculateQueueSizeClicked)="onCalculateQueueSizeClicked($event)"
          (deleteClicked)="onDeleteClicked()"
          (restoreClicked)="onRestoreClicked()"
          (deeplinkClicked)="onDeeplinkClicked($event)"
          (configCheckClicked)="onConfigCheckClicked()"
      ></IntegrationGridItem>

    } @else if (type() === 'list') {
      <IntegrationListItem
          [firstApp]="firstApp()"
          [secondApp]="secondApp()"
          [description]="description()"
          [integration]="integration()"
          [status]="getStatus(integration()?.status)"
          (orderButtonClicked)="onOrderButtonClicked()"
      ></IntegrationListItem>
    }
  `,
})
export class Integration {
  readonly firstApp = input<AppIntegrationFromTo>();
  readonly secondApp = input<AppIntegrationFromTo>();
  readonly description = input('');
  readonly hasClickHandler = input(false);
  readonly showTryNowButton = input(true);
  readonly readonly = input<boolean>();
  readonly showHistoryButton = input<boolean>();
  readonly href = input('');
  readonly integration = input<AppIntegration>();
  readonly type = input('grid');
  clicked = output<void>();
  orderButtonClicked = output<void>();
  runClicked = output<UserCombi>();
  historyClicked = output<UserCombi>();
  statisticsClicked = output<void>();
  calculateQueueSizeClicked = output<UserCombi>();
  abandonedCartStatisticsClicked = output<void>();
  editClicked = output<void>();
  deleteClicked = output<void>();
  restoreClicked = output<void>();
  deeplinkClicked = output<string>()
  configCheckClicked = output<void>();

  constructor(private userCombiPackageService: UserCombiPackageService) {
  }

  getStatus(status: string) {
    if (!status) {
      return 'active';
    }

    switch (status) {
      case 'trial-running':
        return 'active';
      case 'trial-ended':
        return 'disabled';
      default:
        return status;
    }
  }

  onClicked(): void {
    this.clicked.emit();
  }

  onOrderButtonClicked(): void {
    this.orderButtonClicked.emit();
  }

  async onToggleActiveClicked() {
    const refreshedPackages = await this.userCombiPackageService.toggleActive(this.integration().userCombiPackage.id);
    this.integration().userCombiPackage.active = refreshedPackages.filter(pkg => pkg.id === this.integration().userCombiPackage.id)[0].active;
  }

  onRunClicked(userCombi: UserCombi) {
    this.runClicked.emit(userCombi);
  }

  onHistoryClicked(userCombi: UserCombi) {
    this.historyClicked.emit(userCombi);
  }

  onStatisticsClicked() {
    this.statisticsClicked.emit();
  }

  onCalculateQueueSizeClicked(userCombi: UserCombi) {
    this.calculateQueueSizeClicked.emit(userCombi);
  }

  onAbandonedCartStatisticsClicked() {
    this.abandonedCartStatisticsClicked.emit();
  }

  onEditClicked() {
    this.editClicked.emit();
  }

  onDeleteClicked() {
    this.deleteClicked.emit();
  }

  onRestoreClicked() {
    this.restoreClicked.emit();
  }

  onDeeplinkClicked(stepName: string) {
    this.deeplinkClicked.emit(stepName);
  }

  onConfigCheckClicked() {
    this.configCheckClicked.emit();
  }
}
